import { locationTypesQuery } from "@/api/location";
import { purchasesQuery, PurchaseStatus, PurchaseType } from "@/api/sales";
import { DataTable } from "@/components/data-table/DataTable";
import { ColumnDef, DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableFooter } from "@/components/data-table/DataTableFooter";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { CancelBookingDialog } from "@/components/purchases/CancelBookingDialog";
import { RefundPurchaseDialog } from "@/components/purchases/RefundPurchaseDialog";
import { Badge } from "@/components/ui/Badge";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import { ButtonLink } from "@/components/ui/Link";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { getTokenCurrencyCodes, TokenCurrencyCode } from "@/modules/money-fns";
import { seo } from "@/utils/seo";
import { formatDateTime } from "@common/utils/date";
import { formatCents } from "@common/utils/i18n";
import { titleCase } from "@common/utils/string";
import { Box, Center, Flex, QueryHandler, Text } from "@givenwell/components";
import { DotsIcon } from "@givenwell/core-solid-icons";
import { PurchaseBrief } from "@givenwell/management-api";
import { CSS } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { array, fallback, number, object, optional, pipe, string, toMinValue, transform } from "valibot";
import { IdAndName, IdAndNameFilter } from "./IdAndNameFilter";

const PURCHASE_TYPES: IdAndName[] = [
  { id: "booking", name: "Booking" },
  { id: "order", name: "Order" },
  { id: "ticket", name: "Ticket" },
  { id: "subscription", name: "Subscription" },
  { id: "pass", name: "Pass" },
  { id: "eap_session", name: "EAP Session" },
];

const PURCHASE_STATUSES: IdAndName[] = [
  { id: "upcoming", name: "Upcoming" },
  { id: "cancelled", name: "Cancelled" },
  { id: "disputed", name: "Disputed" },
  { id: "confirmed", name: "Confirmed" },
  { id: "refunded", name: "Refunded" },
];

const SEARCH_PURCHASE_STATUSES = ["cancelled", "confirmed", "disputed", "refunded", "upcoming"] as PurchaseStatus[];
const SEARCH_PURCHASE_TYPES = ["booking", "subscription", "order", "pass", "ticket", "eap_session"] as PurchaseType[];

function optionalStringToOptionalDate(dateStr: string = "") {
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }
  return undefined;
}

export const Route = createFileRoute("/supplier/$supplierId/sales/")({
  component: SupplierSalesLoader,
  validateSearch: object({
    q: fallback(optional(string(), ""), ""),
    page: fallback(pipe(optional(number(), 1), toMinValue(1)), 1),
    "page-size": fallback(pipe(optional(number(), 100), toMinValue(10)), 100),
    start: fallback(pipe(optional(string()), transform(optionalStringToOptionalDate)), undefined),
    end: fallback(pipe(optional(string()), transform(optionalStringToOptionalDate)), undefined),
    "purchase-statuses": fallback(optional(array(string()), SEARCH_PURCHASE_STATUSES), SEARCH_PURCHASE_STATUSES),
    "purchase-types": fallback(optional(array(string()), SEARCH_PURCHASE_TYPES), SEARCH_PURCHASE_TYPES),
  }),
  head: () => ({
    meta: seo({ title: "Sales" }),
  }),
});

function SupplierSalesLoader() {
  const { supplierId } = Route.useParams();
  const locationTypes = useQuery(locationTypesQuery);

  return (
    <PageWrapper title="Sales" maxWidth={1400}>
      <Flex css={{ flexDirection: "column", gap: 20 }}>
        <QueryHandler
          query={locationTypes}
          success={({ data: locationTypes }) => (
            <SalesTable
              supplierId={supplierId}
              purchaseTypes={PURCHASE_TYPES}
              purchaseStatuses={PURCHASE_STATUSES}
              locationTypes={locationTypes}
            />
          )}
        />
      </Flex>
    </PageWrapper>
  );
}

type SalesTableProps = {
  supplierId: string;
  purchaseTypes: IdAndName[];
  purchaseStatuses: IdAndName[];
  locationTypes: IdAndName[];
};
function SalesTable({ supplierId, purchaseTypes, purchaseStatuses }: SalesTableProps) {
  const search = Route.useSearch();
  const { page, "page-size": pageSize, start, end, "purchase-statuses": statuses, "purchase-types": types } = search;
  const allStatusesSelected = statuses.length === purchaseStatuses.length;
  const allTypesSelected = types.length === purchaseTypes.length;
  const navigate = useNavigate();

  const purchaseQuery = useQuery(
    purchasesQuery(
      supplierId,
      allStatusesSelected ? undefined : (statuses as PurchaseStatus[]),
      allTypesSelected ? undefined : (types as PurchaseType[]),
      undefined, // Pass statuses.
      page,
      pageSize,
      start,
      end,
    ),
  );

  const includeBookingFields = purchaseQuery.data?.purchases.some(row => !!row.booking) ?? false;
  const columns = usePurchaseColumns(includeBookingFields);

  const hasFilters = !allStatusesSelected || !allTypesSelected || !!start || !!end;

  function handleClassificationCommitted(types: string[]) {
    navigate({
      to: ".",
      params: true,
      search: {
        ...search,
        start: start?.toISOString(),
        end: end?.toISOString(),
        "purchase-types": types as PurchaseType[],
      },
    });
  }

  function handleStatusesCommitted(statuses: string[]) {
    navigate({
      to: ".",
      params: true,
      search: {
        ...search,
        start: start?.toISOString(),
        end: end?.toISOString(),
        "purchase-statuses": statuses as PurchaseStatus[],
      },
    });
  }

  return (
    <DataTable>
      <DataTableHeader
        hideSearch
        hideToggle
        hideClear
        rightContent={
          <ButtonLink
            size="sm"
            to="/supplier/$supplierId/payments"
            params={{ supplierId }}
            variant="plain"
            color="primary"
          >
            View payouts
          </ButtonLink>
        }
      >
        <IdAndNameFilter label="Type" options={purchaseTypes} value={types} onCommit={handleClassificationCommitted} />
        <IdAndNameFilter
          label="Status"
          options={purchaseStatuses}
          value={statuses}
          onCommit={handleStatusesCommitted}
        />
        <DateRangePicker
          placeholder="Purchase date"
          value={{ start, end }}
          onValueChange={({ start, end }) => {
            navigate({
              to: ".",
              params: true,
              search: {
                ...search,
                start: start?.toISOString(),
                end: end?.toISOString(),
              },
            });
          }}
          maxDate={new Date()}
        />
        <Box css={{ flexGrow: 1 }} />
      </DataTableHeader>
      <DataTableBody
        data={purchaseQuery.data?.purchases || []}
        columns={columns}
        dataKey="id"
        empty={
          hasFilters ? null : (
            <Center css={{ h: 256, w: "100%", p: 16 }}>
              <Text css={{ fontScale: "sm", color: colors.text2, align: "center", textWrap: "balance" }}>
                When your customers make purchases, they will appear here.
              </Text>
            </Center>
          )
        }
        status={purchaseQuery.status}
      />
      <DataTableFooter itemCount={purchaseQuery.data?.total_count} />
    </DataTable>
  );
}

function usePurchaseColumns(includeBookingFields: boolean) {
  const navigate = useNavigate({
    from: "/supplier/$supplierId/sales",
  });
  const { supplierId } = Route.useParams();
  const [cancelDialogOpen, setCancelDialogOpen] = useState("");
  const [refundDialogOpen, setRefundDialogOpen] = useState("");
  const bookingFields: ColumnDef<PurchaseBrief>[] = includeBookingFields
    ? [
        {
          header: "Booking Date",
          cell: item =>
            item.booking?.start_timestamp ? `${formatDateTime(new Date(item.booking.start_timestamp)) ?? ""}` : "",
        },
        {
          header: "Resource",
          cell: item => `${item.booking?.resource_name ?? "N/A"}`,
        },
      ]
    : [];

  const columns: ColumnDef<PurchaseBrief>[] = [
    {
      header: "Order #",
      cell: sale => (
        <StyledLink
          to="/supplier/$supplierId/sales/$purchaseId"
          params={{ supplierId: supplierId, purchaseId: sale.id }}
        >
          {sale.friendly_id}
        </StyledLink>
      ),
    },
    {
      header: "Date",
      cell: "purchased_at",
      formatter: "date",
    },
    {
      header: "Title",
      cell: "listing_title",
    },
    {
      header: "Variant",
      cell: "variant_title",
    },
    {
      header: "Type",
      cell: purchase => (purchase.purchase_type === "eap_session" ? "EAP" : titleCase(purchase.purchase_type)),
    },
    {
      header: "Purchaser",
      cell: item => (
        <>
          {item.purchaser_name} {item.eap_used && <Badge color="green">EAP</Badge>}
        </>
      ),
    },
    ...bookingFields,
    {
      header: "SKU",
      cell: "supplier_sku_code",
    },
    {
      header: "Status",
      cell: purchase => <PurchaseStatusBadge status={purchase.purchase_status} />,
    },
    {
      header: "Payout",
      cell: item => {
        if (item.purchase_status === "cancelled") {
          return null;
        }

        if (!getTokenCurrencyCodes().includes(item.token_currency_code as TokenCurrencyCode)) {
          return "N/A";
        }

        return formatCents(item.cost_price_cents, item.cost_price_currency);
      },
      css: {
        textAlign: "right",
      },
    },
    {
      header: "Paid?",
      cell: purchase => (purchase.payout_status === "paid" ? "Yes" : "No"),
    },
    {
      header: undefined,
      columnKey: "actions",
      css: {
        width: "80px",
      },
      cell: purchase => (
        <>
          <DropdownMenu
            content={
              <>
                {purchase.purchase_status === "upcoming" && (
                  <>
                    <DropdownMenuItem
                      onSelect={() => {
                        requestAnimationFrame(() => {
                          setCancelDialogOpen(purchase.id);
                        });
                      }}
                    >
                      Cancel Booking
                    </DropdownMenuItem>
                    <DropdownMenuSeparator css={{ my: 8 }} />
                  </>
                )}
                {purchase.purchase_status === "confirmed" &&
                  (purchase.payout_status === "pending" || purchase.payout_status === "ready") && (
                    <>
                      <DropdownMenuItem
                        onSelect={() => {
                          requestAnimationFrame(() => {
                            setRefundDialogOpen(purchase.id);
                          });
                        }}
                      >
                        {purchase.eap_used ? "Refund Session" : "Refund Purchase"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator css={{ my: 8 }} />
                    </>
                  )}
                <DropdownMenuItem
                  onSelect={() =>
                    navigate({
                      to: "/supplier/$supplierId/messages",
                      params: params => ({
                        supplierId: params.supplierId,
                      }),
                    })
                  }
                >
                  Message Customer
                </DropdownMenuItem>
              </>
            }
          >
            <IconButton aria-label="Actions" variant="plain" color="neutral">
              <DotsIcon />
            </IconButton>
          </DropdownMenu>
          {purchase.purchase_status === "upcoming" && (
            <CancelBookingDialog
              purchaseId={purchase.id}
              open={purchase.id === cancelDialogOpen}
              onOpenChange={open => setCancelDialogOpen(open ? purchase.id : "")}
            />
          )}
          {purchase.purchase_status === "confirmed" &&
            (purchase.payout_status === "pending" || purchase.payout_status === "ready") && (
              <RefundPurchaseDialog
                purchaseId={purchase.id}
                open={purchase.id === refundDialogOpen}
                onOpenChange={open => setRefundDialogOpen(open ? purchase.id : "")}
              />
            )}
        </>
      ),
    },
  ];

  return columns;
}

function PurchaseStatusBadge({ status, size = "xs" }: { status: PurchaseStatus; size?: "xs" | "sm" }) {
  const css: CSS = size === "xs" ? { fontScale: "xs", height: 16 } : { fontScale: "sm", height: 24 };

  return (
    <Badge
      css={css}
      color={
        {
          completed: "green" as const, // DEPRECATED.
          confirmed: "green" as const,
          cancelled: "gray" as const,
          disputed: "red" as const,
          refunded: "red" as const,
          upcoming: "blue" as const,
        }[status]
      }
    >
      {titleCase(status)}
    </Badge>
  );
}
